import { useState, useEffect } from 'react'
import {
  FileText,
  Send,
  Scale,
  Clock,
  CheckCircle,
  Loader2,
  AlertCircle,
} from 'lucide-react'
import { legalAPI } from '@/utils/api'
import { cn } from '@/lib/utils'

interface LegalProcess {
  rsv_status: string | null
  rsv_company_name: string | null
  mahnschreiben_sent_at: string | null
  klage_filed_at: string | null
  next_deadline: string | null
  next_hearing_date: string | null
}

interface CaseProgressProps {
  caseId: number
}

// Define the stages of a typical EU261 case
const CASE_STAGES = [
  {
    id: 'submitted',
    label: 'Fall eingereicht',
    description: 'Ihr Fall wurde erfolgreich eingereicht',
    icon: FileText,
  },
  {
    id: 'rsv',
    label: 'RSV-Prüfung',
    description: 'Deckungsanfrage bei der Rechtsschutzversicherung',
    icon: AlertCircle,
  },
  {
    id: 'mahnschreiben',
    label: 'Mahnschreiben',
    description: 'Aufforderungsschreiben an die Fluggesellschaft',
    icon: Send,
  },
  {
    id: 'klage',
    label: 'Klage',
    description: 'Gerichtliche Durchsetzung',
    icon: Scale,
  },
  {
    id: 'resolved',
    label: 'Abgeschlossen',
    description: 'Fall abgeschlossen',
    icon: CheckCircle,
  },
]

function formatDate(dateString: string | null): string {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

function getStageStatus(
  stageId: string,
  legalProcess: LegalProcess | null
): 'completed' | 'current' | 'pending' {
  if (!legalProcess) {
    // No legal process yet - just show submitted as completed
    return stageId === 'submitted' ? 'completed' : 'pending'
  }

  // Determine current stage based on legal process data
  const hasRsv = legalProcess.rsv_status && legalProcess.rsv_status !== 'pending'
  const hasMahnschreiben = !!legalProcess.mahnschreiben_sent_at
  const hasKlage = !!legalProcess.klage_filed_at

  switch (stageId) {
    case 'submitted':
      return 'completed'

    case 'rsv':
      if (hasKlage || hasMahnschreiben) return 'completed'
      if (legalProcess.rsv_status === 'approved') return 'completed'
      if (legalProcess.rsv_status === 'denied') return 'completed'
      if (legalProcess.rsv_status === 'partial') return 'completed'
      if (legalProcess.rsv_status === 'pending') return 'current'
      if (legalProcess.rsv_status === 'not_applicable') return 'completed'
      return 'pending'

    case 'mahnschreiben':
      if (hasKlage) return 'completed'
      if (hasMahnschreiben) return 'completed'
      if (hasRsv && !hasMahnschreiben) return 'current'
      return 'pending'

    case 'klage':
      if (hasKlage) return 'current'
      return 'pending'

    case 'resolved':
      return 'pending'

    default:
      return 'pending'
  }
}

export function CaseProgress({ caseId }: CaseProgressProps) {
  const [legalProcess, setLegalProcess] = useState<LegalProcess | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const fetchLegalProcess = async () => {
      setIsLoading(true)
      try {
        const response = await legalAPI.getLegalProcess(caseId)
        setLegalProcess(response)
      } catch (error) {
        // No legal process yet - that's okay
        console.log('No legal process found for case')
        setLegalProcess(null)
      } finally {
        setIsLoading(false)
      }
    }

    fetchLegalProcess()
  }, [caseId])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 text-slate-400 animate-spin" />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
        <Clock className="h-5 w-5 text-blue-500" />
        Fallfortschritt
      </h3>

      {/* Deadline notice */}
      {legalProcess?.next_deadline && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
          <span className="font-medium text-amber-800">Nächste Frist: </span>
          <span className="text-amber-700">{formatDate(legalProcess.next_deadline)}</span>
        </div>
      )}

      {/* Hearing notice */}
      {legalProcess?.next_hearing_date && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-sm">
          <span className="font-medium text-purple-800">Gerichtstermin: </span>
          <span className="text-purple-700">{formatDate(legalProcess.next_hearing_date)}</span>
        </div>
      )}

      {/* Progress steps */}
      <div className="relative">
        {/* Vertical line */}
        <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-slate-200" />

        <div className="space-y-4">
          {CASE_STAGES.map((stage, index) => {
            const status = getStageStatus(stage.id, legalProcess)
            const IconComponent = stage.icon

            return (
              <div key={stage.id} className="relative flex items-start gap-4 pl-2">
                {/* Icon circle */}
                <div
                  className={cn(
                    'relative z-10 w-8 h-8 rounded-full flex items-center justify-center',
                    status === 'completed' && 'bg-green-500',
                    status === 'current' && 'bg-blue-500 ring-4 ring-blue-100',
                    status === 'pending' && 'bg-slate-200'
                  )}
                >
                  <IconComponent
                    className={cn(
                      'h-4 w-4',
                      status === 'completed' && 'text-white',
                      status === 'current' && 'text-white',
                      status === 'pending' && 'text-slate-400'
                    )}
                  />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0 pb-4">
                  <div
                    className={cn(
                      'font-medium',
                      status === 'completed' && 'text-green-700',
                      status === 'current' && 'text-blue-700',
                      status === 'pending' && 'text-slate-400'
                    )}
                  >
                    {stage.label}
                  </div>
                  <div
                    className={cn(
                      'text-sm',
                      status === 'pending' ? 'text-slate-400' : 'text-slate-500'
                    )}
                  >
                    {stage.description}
                  </div>

                  {/* Status-specific info */}
                  {stage.id === 'rsv' && legalProcess?.rsv_company_name && (
                    <div className="text-sm text-slate-600 mt-1">
                      {legalProcess.rsv_company_name}
                      {legalProcess.rsv_status === 'approved' && (
                        <span className="ml-2 text-green-600 font-medium">(Deckung erteilt)</span>
                      )}
                      {legalProcess.rsv_status === 'denied' && (
                        <span className="ml-2 text-red-600 font-medium">(Deckung abgelehnt)</span>
                      )}
                    </div>
                  )}

                  {stage.id === 'mahnschreiben' && legalProcess?.mahnschreiben_sent_at && (
                    <div className="text-sm text-slate-600 mt-1">
                      Versendet am {formatDate(legalProcess.mahnschreiben_sent_at)}
                    </div>
                  )}

                  {stage.id === 'klage' && legalProcess?.klage_filed_at && (
                    <div className="text-sm text-slate-600 mt-1">
                      Eingereicht am {formatDate(legalProcess.klage_filed_at)}
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
